/** POS V2 / Réparations — suivi SAV aligné sur les statuts métier. */

export const SAV_TIMELINE_STEPS = [
  { id: 'reception', label: 'Réception', shortLabel: 'Réception' },
  { id: 'diagnostic', label: 'Diagnostic', shortLabel: 'Diag.' },
  { id: 'parts_wait', label: 'Attente pièce', shortLabel: 'Attente' },
  { id: 'parts_arrived', label: 'Pièce arrivée', shortLabel: 'Arrivée' },
  { id: 'repair', label: 'Réparation', shortLabel: 'Répar.' },
  { id: 'qa', label: 'Contrôle', shortLabel: 'Contrôle' },
  { id: 'ready', label: 'Prêt à récupérer', shortLabel: 'Prêt' },
];

/** Patterns pour retrouver la date d'une étape dans device.timeline */
const STEP_TIMELINE_MATCHERS = [
  (t) => /dépôt|depot créé|reçu|recu/i.test(t) && !/attente.*piè|attente.*piece/i.test(t),
  (t) => /diagnostic/i.test(t),
  (t) => /attente.*piè|attente.*piece|attente pcs/i.test(t),
  (t) => /pièce arriv|piece arriv|pieces arriv/i.test(t),
  (t) => /en cours|réparation|reparation/i.test(t) && !/attente/i.test(t),
  (t) => /qualité|qualite|contrôle|controle|test qualité/i.test(t),
  (t) => /terminé|termine|prêt|pret/i.test(t) && !/non/i.test(t),
];

export function normalizeRepairStatus(status) {
  return (status || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function isTerminalFailureStatus(status) {
  const s = normalizeRepairStatus(status);
  return s.includes('non reparable') || s.includes('refuse');
}

/**
 * Index de l'étape active (0..steps-1). Si >= steps.length → parcours terminé (ex. Livré).
 */
export function savActiveStepIndex(status) {
  const s = normalizeRepairStatus(status);
  if (!s) return 0;

  if (isTerminalFailureStatus(status)) return 2;

  if (s.includes('livre') || s.includes('recupere')) return SAV_TIMELINE_STEPS.length;
  if (s.includes('termin') && !s.includes('non')) return 6;
  if (s.includes('qualite') || s.includes('controle')) return 5;
  if (s.includes('piece arriv') || s.includes('pieces arriv')) return 3;
  if (s.includes('attente') && (s.includes('piece') || s.includes('pcs'))) return 2;
  if (s.includes('en cours')) return 4;
  if (s.includes('diagnostic')) return 1;
  if (s.includes('attente') && s.includes('depot')) return 0;
  if (s.includes('recu')) return 1;
  if (s.includes('depot')) return 0;
  if (s.includes('pret')) return 6;
  if (s.includes('attente')) return 2;

  return 1;
}

export function savTimelineView(device) {
  const status = device?.status || '';
  const rawActive = savActiveStepIndex(status);
  const stepCount = SAV_TIMELINE_STEPS.length;
  const allDone = rawActive >= stepCount;
  const activeIndex = allDone ? stepCount - 1 : rawActive;
  const progressPct = allDone
    ? 100
    : Math.round((rawActive / Math.max(stepCount - 1, 1)) * 100);

  return {
    status,
    rawActive,
    activeIndex,
    allDone,
    progressPct,
    isFailure: isTerminalFailureStatus(status),
  };
}

export function formatPhoneDisplay(phone) {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  }
  return phone;
}

export function repairFinancials(device) {
  if (!device) {
    return { total: 0, acompte: 0, reste: 0 };
  }
  const total = Number(device.price_repair || 0)
    + Number(device.price_diagnostic || 0)
    - Number(device.remise || 0);
  const acompte = Number(device.total_acomptes || 0);
  const reste = device.reste_a_payer != null
    ? Number(device.reste_a_payer)
    : Math.max(0, total - acompte);
  return { total, acompte, reste };
}

export function savStatusTone(status) {
  const s = normalizeRepairStatus(status);
  if (s.includes('termin') && !s.includes('non')) {
    return { label: status || '—', className: 'bg-emerald-900/60 text-emerald-200 border-emerald-500/40' };
  }
  if (s.includes('livre') || s.includes('recupere')) {
    return { label: status || '—', className: 'bg-gray-700/80 text-gray-200 border-gray-500/40' };
  }
  if (s.includes('piece arriv')) {
    return { label: status || '—', className: 'bg-cyan-900/60 text-cyan-200 border-cyan-500/40' };
  }
  if (s.includes('attente') && (s.includes('piece') || s.includes('pcs'))) {
    return { label: status || '—', className: 'bg-blue-900/60 text-blue-200 border-blue-500/40' };
  }
  if (s.includes('attente')) {
    return { label: status || '—', className: 'bg-blue-900/60 text-blue-200 border-blue-500/40' };
  }
  if (s.includes('non reparable') || s.includes('refuse')) {
    return { label: status || '—', className: 'bg-red-950/80 text-red-300 border-red-500/50' };
  }
  if (s.includes('en cours') || s.includes('recu')) {
    return { label: status || '—', className: 'bg-amber-900/60 text-amber-200 border-amber-500/40' };
  }
  return { label: status || '—', className: 'bg-white/10 text-gray-200 border-white/20' };
}

export function stepTimestamp(device, stepIndex) {
  const timeline = device?.timeline || [];
  const matcher = STEP_TIMELINE_MATCHERS[stepIndex];

  if (matcher && timeline.length) {
    for (const entry of timeline) {
      const blob = `${entry.label || ''} ${entry.status || ''}`;
      if (matcher(blob)) return entry.timestamp;
    }
  }

  if (stepIndex === 0) return device?.created_at || null;

  const { rawActive, allDone } = savTimelineView(device);
  if (stepIndex === rawActive || (allDone && stepIndex === SAV_TIMELINE_STEPS.length - 1)) {
    return device?.updated_at || device?.created_at || null;
  }
  return null;
}

export function formatSavDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function buildSavCartLine(device, amount, kind = 'acompte') {
  const brand = device.model || device.device || '';
  const label = kind === 'reste' ? 'Solde réparation' : 'Acompte réparation';
  const safeAmount = Number(amount);
  if (!device?.ref || !safeAmount || safeAmount <= 0) return null;
  return {
    produit_ref: `ACOMPTE-REP-${device.ref}`,
    designation: `${label} ${device.ref}${brand ? ` — ${brand}` : ''}`,
    quantite: 1,
    prix_unitaire: safeAmount,
    prix_total: safeAmount,
    stock_magasin_id: null,
    is_acompte: true,
    device_ref: device.ref,
  };
}

export function matchesSavSearch(device, query) {
  if (!query?.trim()) return true;
  const q = query.trim().toLowerCase();
  const haystack = [
    device.ref,
    device.client_name,
    device.client_phone,
    device.model,
    device.problem,
    device.status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}
